import { useGetTestPrepPanelStatsQuery } from '~/statistics-v2/api/statistics.api'

export interface TestPrepPanelFigures {
  isLoading: boolean
  testsTaken: number
  /** Σ sub-answers over completed tests (the score's denominator) — added to the header's Activities. */
  activities: number
  /** Points earned — the accuracy numerator contribution. */
  points: number
  /** Points possible (server-derived from each test's sub-answers) — the denominator contribution. */
  possiblePoints: number
  /** XP earned on these tests — added to the header's Total XP on single-grade views. */
  xp: number
  timeSpentMs: number
}

const EMPTY_FIGURES = { testsTaken: 0, activities: 0, points: 0, possiblePoints: 0, xp: 0, timeSpentSeconds: 0 }

/**
 * Test Prep sums for one panel selection, scoped to the panel's curriculum country. Raw
 * numerators/denominators, not percentages — the caller blends them into the header figures.
 * Meant for single-grade selections only: the All-grades view already contains test prep through
 * the unmapped-activities bucket, so blending there would double-count.
 *
 * Points/possible points follow completeTest's own denominator (sub-answers per exercise), NOT
 * number_of_questions — scores are point sums and can exceed the exercise count.
 */
export function useTestPrepPanelStats(
  selectedGradeKey: string,
  countryCode: string,
  familyMemberId?: number,
): TestPrepPanelFigures {
  const { data, isLoading } = useGetTestPrepPanelStatsQuery({ familyMemberId })

  const selected = (data?.byGrade ?? []).filter(
    (stat) =>
      (!countryCode || stat.countryCode === countryCode) &&
      (selectedGradeKey === '0' || stat.grade === Number(selectedGradeKey)),
  )

  const totals = selected.reduce(
    (acc, stat) => ({
      testsTaken: acc.testsTaken + stat.testsTaken,
      activities: acc.activities + stat.activities,
      points: acc.points + stat.points,
      possiblePoints: acc.possiblePoints + stat.possiblePoints,
      xp: acc.xp + stat.xp,
      timeSpentSeconds: acc.timeSpentSeconds + stat.timeSpentSeconds,
    }),
    EMPTY_FIGURES,
  )

  return {
    isLoading,
    testsTaken: totals.testsTaken,
    activities: totals.activities,
    points: totals.points,
    possiblePoints: totals.possiblePoints,
    xp: totals.xp,
    timeSpentMs: totals.timeSpentSeconds * 1000,
  }
}
